import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { mapCompanySecretaryFromApi } from '../../utils/apiMappers';
import AccountSecuritySection from '../../components/users/AccountSecuritySection';
import AccountCreatedDialog from '../../components/users/AccountCreatedDialog';
import {
  shouldShowAccountSetupDialog,
  accountSetupSuccessMessage,
} from '../../utils/accountSetupDisplay';

const CreateCompanySecretary = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [error, setError] = useState('');
  const [accountSetup, setAccountSetup] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (!isEditMode) {
      setFetching(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setFetching(true);
        setError('');
        const res = await csAdminApi.getCompanySecretaryById(id);
        const data = mapCompanySecretaryFromApi(res.data?.data);
        if (!data) {
          if (!cancelled) setError('Company secretary not found');
          return;
        }
        if (!cancelled) {
          setFormData((prev) => ({
            ...prev,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load company secretary');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await csAdminApi.updateCompanySecretary(id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mfaEmailOtp: true,
        });
        enqueueSnackbar('Company Secretary updated successfully', { variant: 'success' });
        navigate(`/company-secretaries/${id}`);
        return;
      }

      const response = await csAdminApi.createCompanySecretary({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        mfaEmailOtp: true,
        sendWelcomeEmail: true,
      });

      const setup = response.data?.data?.accountSetup;
      if (setup && shouldShowAccountSetupDialog(setup)) {
        setAccountSetup(setup);
        setAccountDialogOpen(true);
      } else {
        enqueueSnackbar(accountSetupSuccessMessage(setup), { variant: 'success' });
        navigate('/company-secretaries');
      }
    } catch (err) {
      const actionLabel = isEditMode ? 'update' : 'create';
      console.error(`Error ${actionLabel} company secretary:`, err);
      const apiMessage = err.response?.data?.error?.message || err.response?.data?.message;
      const fallback = isEditMode
        ? 'Failed to update company secretary'
        : 'Failed to create company secretary';
      setError(apiMessage || fallback);
      enqueueSnackbar(apiMessage || fallback, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(isEditMode ? `/company-secretaries/${id}` : '/company-secretaries');
  };

  if (fetching) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Company Secretary' : 'Create Company Secretary Account'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isEditMode
            ? 'Update company secretary profile details'
            : 'Add a new company secretary. Login username will be their work email; a welcome email with the password setup link will be sent automatically.'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Personal Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email || 'Used as login username and for OTP delivery'}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                placeholder="+971 50 123 4567"
              />
            </Grid>
          </Grid>

          {!isEditMode && (
            <>
              <Divider sx={{ my: 4 }} />
              <AccountSecuritySection />
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {isEditMode ? 'Save Changes' : 'Create Account'}
            </Button>
          </Box>
        </form>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="info.dark" gutterBottom>
            Note:
          </Typography>
          <Typography variant="caption" color="info.dark">
            After creating the account, assign this company secretary to companies from the Companies page or from the secretary detail page.
          </Typography>
        </Box>
      </Paper>

      <AccountCreatedDialog
        open={accountDialogOpen}
        onClose={() => {
          setAccountDialogOpen(false);
          navigate('/company-secretaries');
        }}
        title="Company Secretary Account Created"
        subtitle="Save these credentials now. They will not be shown again."
        accountSetup={accountSetup}
      />
    </Container>
  );
};

export default CreateCompanySecretary;
